/** Public library information, plus the staff editing mutation. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { get, patch, patchForm } from '@/lib/api'
import { queryKeys, queryScopes } from '@/lib/queryKeys'
import type { LibraryInfo } from '@/types/api'

import { useLanguage } from './useDirection'

export const useLibraryInfo = () => {
  const language = useLanguage()
  return useQuery({
    queryKey: queryKeys.libraryInfo(language),
    queryFn: () => get<LibraryInfo>('/library/info/'),
    // Opening hours and contact details change rarely.
    staleTime: 5 * 60_000,
  })
}

export const useUpdateLibraryInfo = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (payload: Partial<Record<keyof LibraryInfo, unknown>>) =>
      patch<LibraryInfo>('/library/info/', payload),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryScopes.libraryInfo })
    },
  })
}

/** Upload a new church logo, or remove it with `null`. Staff only. */
export const useUpdateLibraryLogo = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (file: File | null) => {
      const form = new FormData()
      if (file) form.append('logo', file)
      else form.append('remove_logo', 'true')
      return patchForm<LibraryInfo>('/library/info/', form)
    },
    // Returned, so the caller's onSuccess runs once the header has the new logo.
    onSuccess: () => client.invalidateQueries({ queryKey: queryScopes.libraryInfo }),
  })
}
